import { Link } from "react-router-dom";
import { useBrands } from "@/hooks/use-cms-api";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ArrowRight, Building2, Award } from "lucide-react";

export const FeaturedBrands = () => {
  const { data: brands, isLoading } = useBrands();

  // Show top 6 brands (they're already sorted by name)
  const featuredBrands = brands?.slice(0, 6) || [];

  if (isLoading) {
    return (
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Skeleton className="h-6 w-32 mx-auto mb-4" />
            <Skeleton className="h-10 w-64 mx-auto mb-4" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!featuredBrands.length) {
    return null;
  }

  return (
    <section className="py-16 bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge variant="secondary" className="mb-4">
            <Award className="h-3 w-3 mr-2" />
            Trusted Partners
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Premium Brands We Trust
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We partner with industry-leading supplement manufacturers known for quality, transparency, and science-backed formulations
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 mb-10">
          {featuredBrands.map((brand) => (
            <HoverCard key={brand.id} openDelay={200} closeDelay={100}>
              <HoverCardTrigger asChild>
                <Link
                  to={`/brand/${brand.slug}`}
                  className="group bg-card rounded-xl border hover:border-primary/50 hover:shadow-lg transition-all duration-300 p-6 flex flex-col items-center justify-center min-h-[120px]"
                >
                  {brand.logo_url ? (
                    <img
                      src={brand.logo_url}
                      alt={`${brand.name} logo`}
                      className="h-10 max-w-[120px] object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`flex flex-col items-center ${brand.logo_url ? 'hidden' : ''}`}>
                    <Building2 className="h-8 w-8 text-muted-foreground/50 group-hover:text-primary/70 transition-colors mb-2" />
                    <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors text-center">
                      {brand.name}
                    </span>
                  </div>
                  {brand.logo_url && (
                    <span className="text-xs font-medium text-muted-foreground group-hover:text-primary transition-colors mt-3 text-center">
                      {brand.name}
                    </span>
                  )}
                </Link>
              </HoverCardTrigger>
              <HoverCardContent className="w-72" align="center">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {brand.logo_url ? (
                      <img
                        src={brand.logo_url}
                        alt={brand.name}
                        className="h-6 w-auto object-contain"
                      />
                    ) : (
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                    )}
                    <h4 className="text-sm font-semibold">{brand.name}</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {brand.description || `Explore premium supplements from ${brand.name}, crafted with quality ingredients for optimal health.`}
                  </p>
                  <div className="flex items-center text-xs text-primary">
                    View all products
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>
          ))}
        </div>

        <div className="text-center">
          <Button variant="outline" size="lg" asChild>
            <Link to="/brands">
              View All Brands
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
